import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Star } from 'lucide-react';

function ReviewCard({ review, idx }) {
  return (
    <Card className="p-6 border border-border" data-testid={`review-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium">{review.tenant_name}</div>
          <div className="flex items-center gap-1 mt-1">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
            ))}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{new Date(review.created_at).toLocaleDateString()}</div>
      </div>
      <p className="text-muted-foreground leading-relaxed">{review.comment}</p>
    </Card>
  );
}

export default function ReviewsSection({ reviews, canReview, onSubmitReview }) {
  const [showDialog, setShowDialog] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });

  const submit = async () => {
    const ok = await onSubmitReview(reviewData);
    if (ok) {
      setShowDialog(false);
      setReviewData({ rating: 5, comment: '' });
    }
  };

  return (
    <div className="mt-16 border-t border-border pt-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Reviews ({reviews.length})</h2>
        {canReview && (
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="write-review-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                <Star className="w-4 h-4 mr-2" />Write Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Write a Review</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Rating</Label>
                  <div className="flex gap-2 mt-2">
                    {[1,2,3,4,5].map(i => (
                      <button key={i} data-testid={`review-star-${i}`} onClick={() => setReviewData({ ...reviewData, rating: i })}>
                        <Star className={`w-8 h-8 ${i <= reviewData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Comment</Label>
                  <Textarea data-testid="review-comment-textarea" value={reviewData.comment} onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })} rows={4} className="mt-2" />
                </div>
                <Button data-testid="review-submit-btn" onClick={submit} className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                  Submit Review
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {reviews.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed">
          <Star className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((review, idx) => (
            <ReviewCard key={review.id} review={review} idx={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
